import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import Form from '@nineeng/forms';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { ConditionalTaskWrapper } from '@repo/components';
import { Task } from '@repo/shared/domain';
import { AccountSetupForm } from '@repo/forms';
import { determineUserProps, EventEmitter } from '@repo/shared/helpers';
import {
	analytics,
	createFieldChangeHandler,
	createSectionNavigateHandler,
	identifyActor,
	taskUserProps,
} from '@repo/shared/analytics';
import { appTheme } from '@repo/theme';
import { getTask, updateTask, saveTaskDraft } from 'src/api/tasks';
import { useAccessThemeWithV2Neutral } from 'src/hooks/useAccessThemeWithV2Neutral';
import { NEUTRAL_ACCESS_ACCENT, NEUTRAL_ACCESS_TEXT } from 'src/utils/accessThemeNeutral';

interface SearchParams {
	auth?: string;
	workforceAuth?: string;
	deviceId?: string;
	deviceToken?: string;
	lang?: string;
	lng?: string;
	companyId?: string;
	userId?: string;
	primaryColor?: string;
	textColor?: string;
	forCompanyPortal?: boolean;
}

function formHasTestSection(formSchema: unknown): boolean {
	if (!formSchema || typeof formSchema !== 'object') return false;
	const schema = formSchema as { sections?: { type?: string }[]; type?: string };
	if (schema.sections?.some((section) => section.type === 'TestSection')) return true;
	return schema.type === 'TestSection';
}

export const Route = createFileRoute('/app/tasks/$taskId')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		auth: search.auth as string | undefined,
		workforceAuth: search.workforceAuth as string | undefined,
		deviceId: search.deviceId as string | undefined,
		deviceToken: search.deviceToken as string | undefined,
		lang: search.lang as string | undefined,
		lng: search.lng as string | undefined,
		companyId: search.companyId as string | undefined,
		userId: search.userId as string | undefined,
		primaryColor: search.primaryColor as string | undefined,
		textColor: search.textColor as string | undefined,
		forCompanyPortal: search.forCompanyPortal === true || search.forCompanyPortal === 'true',
	}),
	loaderDeps: ({ search }) => ({
		workforceAuth: search.workforceAuth,
		deviceId: search.deviceId,
		deviceToken: search.deviceToken,
		auth: search.auth,
		lang: search.lang,
		lng: search.lng,
		forCompanyPortal: search.forCompanyPortal,
	}),
	loader: async ({ params, deps }) => {
		const { taskId } = params;
		const language =
			(deps.lang && deps.lang !== 'undefined' ? deps.lang : null) ||
			(deps.lng && deps.lng !== 'undefined' ? deps.lng : null) ||
			'';
		const deviceAuth =
			deps.deviceId && deps.deviceToken ? `deviceId=${deps.deviceId}&deviceToken=${deps.deviceToken}` : '';
		const basicAuth = deps.auth ? `Basic ${deps.auth}` : null;
		const workforceAuth = deps.workforceAuth ? `Bearer ${deps.workforceAuth}` : null;
		const taskQuery = language ? `${deviceAuth}&lng=${language}` : deviceAuth;
		const forCompanyPortal = !!deps.forCompanyPortal;

		// Prefer the workforce (Bearer) token over basic auth when both are present.
		// Use the capitalized `Authorization` key so it overrides the request client's
		// default header instead of producing a duplicate (which fetch would otherwise
		// fold into a single "undefined, Bearer ..." value).
		const authorization = workforceAuth || basicAuth;

		try {
			const task = await getTask({
				taskId,
				query: taskQuery || undefined,
				...(authorization ? { headers: { Authorization: authorization } } : {}),
			});
			return { task, taskQuery, basicAuth, authorization, language, deviceAuth, forCompanyPortal };
		} catch (error) {
			return {
				task: undefined,
				taskQuery,
				basicAuth,
				authorization,
				language,
				deviceAuth,
				error,
				forCompanyPortal,
			};
		}
	},
	component: TaskPage,
});

function TaskPage() {
	const { taskId } = Route.useParams();
	const { primaryColor, textColor } = Route.useSearch();
	const { isV2Default } = useAccessThemeWithV2Neutral(undefined);

	const taskFormNavigationColors = useMemo(() => {
		if (isV2Default) return { next: NEUTRAL_ACCESS_ACCENT, text: NEUTRAL_ACCESS_TEXT };
		if (primaryColor) return { next: primaryColor, text: textColor };
		return undefined;
	}, [isV2Default, primaryColor, textColor]);

	const {
		task: initialTask,
		taskQuery,
		basicAuth,
		authorization,
		language,
		deviceAuth,
		error: loaderError,
		forCompanyPortal,
	} = Route.useLoaderData();

	const [isClient, setIsClient] = useState(false);
	const [isUploading, setUploading] = useState(false);

	const taskAnalyticsProps = useMemo(
		() => ({
			task_id: taskId,
			task_name: initialTask?.name,
			task_type: (initialTask as any)?.template?.type,
			template_slug: initialTask?.templateSlug,
			source: 'iframe' as const,
			...taskUserProps(initialTask?.user),
		}),
		[taskId, initialTask?.name, initialTask?.user?.id],
	);

	useEffect(() => {
		identifyActor(initialTask?.user);
	}, [initialTask?.user?.id]);

	useEffect(() => {
		if (initialTask) {
			analytics.capture('task_opened', { ...taskAnalyticsProps, task_status: initialTask.status });
		}
		const handler = () => {
			analytics.capture('task_abandoned', taskAnalyticsProps);
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [taskId]);

	// Send isReady message to parent immediately
	useEffect(() => {
		window.parent.postMessage({ isReady: true }, '*');
		setIsClient(true);
	}, []);

	// Fetch task data with initial data from loader
	const {
		data: task,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['task', taskId, taskQuery],
		queryFn: () =>
			getTask({
				taskId,
				query: taskQuery || undefined,
				...(authorization ? { headers: { Authorization: authorization } } : {}),
			}),
		initialData: initialTask,
	});

	const taskRefData = useMemo(
		() => ({
			user: task?.user,
			group: task?.group,
			event: task?.event,
		}),
		[task],
	);

	const taskData = useMemo(() => {
		if (!task) return null;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { user, group, event, ...rest } = task;
		return rest as Omit<Task, 'user' | 'group' | 'event'> & {
			Form?: { availableLanguages?: string[]; language?: string; formSchema?: unknown; saveDrafts?: boolean };
			executePermission?: { isAllowed: boolean; denyReason?: string };
		};
	}, [task]);

	const availableLanguages = useMemo(() => {
		const langs: string[] = [];
		if (taskData?.Form?.availableLanguages) {
			langs.push(...taskData.Form.availableLanguages.map((l) => l.toLowerCase()));
		}
		if (taskData?.Form?.language) {
			langs.push(taskData.Form.language.toLowerCase());
		}
		return langs;
	}, [taskData]);

	// The company portal is english only. If a task has an english translation we show that.
	let shouldChooseLanguage = false;
	let formLanguage = language;
	if (forCompanyPortal) {
		// if only one language is available we use that.
		if (availableLanguages.length === 1) {
			formLanguage = availableLanguages[0] as string;
		} else if (availableLanguages.includes('en')) {
			// If english is among the available languages we use that.
			formLanguage = 'en';
		} else if (!formLanguage && availableLanguages.length > 1) {
			// let the user choose a language
			shouldChooseLanguage = true;
		}
	} else {
		// If one available language is found and queryLanguage was empty, set selectedLanguage to the one found language.
		let selectedLanguage = language.toLowerCase();
		if (!selectedLanguage && availableLanguages.length === 1) {
			selectedLanguage = availableLanguages[0] as string;
		}
		shouldChooseLanguage =
			!!availableLanguages.length && (!selectedLanguage || !availableLanguages.includes(selectedLanguage));
	}

	const originalValues = useMemo(() => {
		if (!taskData) return {};
		return taskData.result
			? {
					...taskData.result,
					taskId: taskData.id,
					userProps: determineUserProps(taskData as Task, taskRefData.user) || {},
				}
			: {
					taskId: taskData.id,
					userProps: determineUserProps(taskData as Task, taskRefData.user) || {
						taskId: taskData.id,
					},
				};
	}, [taskData, taskRefData.user]);

	const handleSubmit = async ({ ...data }: Record<string, unknown>) => {
		if (!taskData) return;
		setUploading(true);
		if (data.urlToOpen) {
			window.parent.postMessage({ urlToOpen: data.urlToOpen }, '*');
			return;
		}

		const t = { ...taskData } as Record<string, unknown>;

		if (taskData.type === 'approval') {
			const approval = data.approval as { status?: string } | undefined;
			t.status = approval?.status === 'approved' ? 'done' : 'failed';
		} else {
			t.status = Object.values(data).find((d) => (d as { isFailed?: boolean })?.isFailed) ? 'failed' : 'done';
		}

		t.result = data;

		try {
			await updateTask(
				taskId,
				t as Partial<Task>,
				deviceAuth || undefined,
				authorization ? { Authorization: authorization } : undefined,
			);
			analytics.capture('task_submitted', taskAnalyticsProps);
			setUploading(false);
			window.parent.postMessage({ close: true, task: true }, '*');
		} catch (e) {
			analytics.capture('task_submit_failed', taskAnalyticsProps);
			console.error(e);
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error submitting task.',
			});
			setUploading(false);
		}
	};

	const shouldCommitForm = useMemo(
		() => !!taskData?.Form?.saveDrafts || formHasTestSection(taskData?.Form?.formSchema),
		[taskData?.Form?.saveDrafts, taskData?.Form?.formSchema],
	);

	const { handleFormChange, handleFormCommit } = useMemo(() => {
		if (!shouldCommitForm) {
			return { handleFormChange: () => {}, handleFormCommit: () => {} };
		}

		const saveDraft = async (data: Record<string, unknown>) => {
			if (!taskData) return;
			const t = { ...taskData, result: data };
			try {
				await saveTaskDraft(
					taskId,
					t as Partial<Task>,
					deviceAuth || undefined,
					authorization ? { Authorization: authorization } : undefined,
				);
				analytics.capture('task_draft_saved', taskAnalyticsProps);
			} catch (e) {
				console.error(e);
			}
		};

		const debouncedSave = debounce(saveDraft, 1000);
		const commitDraft = (data: Record<string, unknown>) => {
			debouncedSave.cancel();
			return saveDraft(data);
		};

		return {
			handleFormChange: taskData?.Form?.saveDrafts ? debouncedSave : () => {},
			handleFormCommit: commitDraft,
		};
	}, [shouldCommitForm, taskData, taskId, deviceAuth, authorization, taskAnalyticsProps]);

	const changeLanguage = (lang: string) => {
		if (language !== lang) {
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.set('lang', lang);
			window.location.href = newUrl.toString();
		}
	};

	if (isLoading) {
		return (
			<ThemeProvider theme={appTheme}>
				<CssBaseline />
				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
					<CircularProgress color="inherit" />
				</Backdrop>
			</ThemeProvider>
		);
	}

	if (error || loaderError) {
		console.error('Task fetch error:', error || loaderError);
		return (
			<div style={{ padding: 20 }}>
				<h2>Error loading task</h2>
				<pre>{String(error || loaderError)}</pre>
			</div>
		);
	}

	if (!task || !taskData) {
		return <div style={{ padding: 20 }}>Task not found!</div>;
	}

	if (!taskData.Form) {
		return <div style={{ padding: 20 }}>Task form not found!</div>;
	}

	if (!isClient) {
		return null;
	}

	return (
		<ThemeProvider theme={appTheme}>
			<CssBaseline />
			<ConditionalTaskWrapper
				condition={!taskData.executePermission?.isAllowed}
				showAlert={false}
				text={taskData.executePermission?.denyReason}
			>
				<div
					className="nine-task-overrides"
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						minHeight: '100%',
						height: 'auto',
						overflow: 'auto',
					}}
				>
					<div style={{ width: '100%', flexGrow: 1, position: 'relative' }}>
						{taskData.type === 'account' ? (
							<AccountSetupForm
								authorization={basicAuth}
								onSubmit={handleSubmit}
								refData={taskRefData as { user?: unknown; group?: unknown; event?: unknown }}
								customNavigationColors={taskFormNavigationColors}
							/>
						) : (
							<Form
								originalValues={originalValues}
								refData={taskRefData}
								schema={taskData.Form?.formSchema}
								onChange={handleFormChange}
								commit={handleFormCommit}
								onSubmit={handleSubmit}
								selectedLanguage={formLanguage}
								disabled={!taskData.executePermission?.isAllowed}
								onFieldChange={createFieldChangeHandler(taskAnalyticsProps)}
								onSectionNavigate={createSectionNavigateHandler(taskAnalyticsProps)}
								customNavigationColors={taskFormNavigationColors}
							/>
						)}
					</div>
				</div>
			</ConditionalTaskWrapper>

			<Backdrop
				sx={{
					color: '#fff',
					zIndex: (theme) => theme.zIndex.drawer + 1,
					display: 'flex',
					flexDirection: 'column',
					padding: 2,
				}}
				open={shouldChooseLanguage}
			>
				This task is not fully available in your preferred language, please select one that is available to continue:
				<div className="flex gap-2 mt-2 flex-wrap justify-evenly">
					{availableLanguages.map((availableLanguage) => (
						<Button
							color="primary"
							variant="outlined"
							key={availableLanguage}
							sx={{ width: 50, height: 50, backgroundColor: 'white' }}
							onClick={() => {
								changeLanguage(availableLanguage);
							}}
						>
							{availableLanguage}
						</Button>
					))}
				</div>
			</Backdrop>

			<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUploading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</ThemeProvider>
	);
}
